import React, { Suspense } from 'react'
import { Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom'
import { SignedIn, SignedOut, useUser, useClerk } from '@clerk/clerk-react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { StripeProvider } from './contexts/StripeContext'
import { useQuery } from 'react-query'
import Layout from './components/Layout'
import { GridProvider } from './contexts/GridContext'
import ImpersonationIndicator from './components/admin/ImpersonationIndicator'
import OnboardingGuard from './components/auth/OnboardingGuard'
import { GuardChain } from './components/auth/guards'
import ErrorBoundary from './components/ErrorBoundary'
import EmailActionHandler from './components/EmailActionHandler'

import { api } from './lib/api'
// import { debugLog } from './utils/debugLogger'
// import DebugPanel from './components/DebugPanel'
import './styles/grid.css'
import { showAlert } from './components/common/AppDialogs';

const PageView = React.lazy(() => import('./pages/PageView'));
const Pages = React.lazy(() => import('./pages/Pages'));
const Forms = React.lazy(() => import('./pages/Forms'));
const Workflows = React.lazy(() => import('./pages/Workflows'));
const Groups = React.lazy(() => import('./pages/Groups'));
const Organisation = React.lazy(() => import('./pages/Organisation'));
const Homepage = React.lazy(() => import('./pages/Homepage'));
const SignInPage = React.lazy(() => import('./pages/SignInPage'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const Search = React.lazy(() => import('./pages/Search'));
const SharedPageView = React.lazy(() => import('./pages/SharedPageView'));

const queryClient = new QueryClient()

function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-lg text-gray-600">Loading...</div>
    </div>
  );
}

// Global error handler for detecting auth loops
function AuthLoopDetector({ children }: { children: React.ReactNode }) {
  const { signOut } = useClerk();
  const { user, isLoaded } = useUser();
  
  // Track page loads, API calls, and URL changes to detect loops
  const loopTrackingRef = React.useRef<{
    userId: string | null;
    pageLoads: number;
    apiErrors: number;
    lastPageLoad: number;
    lastApiError: number;
    urlHistory: string[];
    lastUrlChange: number;
  }>({ userId: null, pageLoads: 0, apiErrors: 0, lastPageLoad: 0, lastApiError: 0, urlHistory: [], lastUrlChange: 0 });

  React.useEffect(() => {
    // Reset tracking when user changes
    if (user?.id !== loopTrackingRef.current.userId) {
      loopTrackingRef.current = {
        userId: user?.id || null,
        pageLoads: 0,
        apiErrors: 0,
        lastPageLoad: 0,
        lastApiError: 0,
        urlHistory: [],
        lastUrlChange: 0
      };
    }

    // Track URL changes to detect redirect loops
    const currentUrl = window.location.href;
    const now = Date.now();
    
    // Add current URL to history
    if (loopTrackingRef.current.urlHistory.length === 0 || 
        loopTrackingRef.current.urlHistory[loopTrackingRef.current.urlHistory.length - 1] !== currentUrl) {
      
      loopTrackingRef.current.urlHistory.push(currentUrl);
      loopTrackingRef.current.lastUrlChange = now;
      
      // Keep only last 10 URLs
      if (loopTrackingRef.current.urlHistory.length > 10) {
        loopTrackingRef.current.urlHistory = loopTrackingRef.current.urlHistory.slice(-10);
      }
      
      // Check for URL bounce pattern (between / and /sign-in)
      if (loopTrackingRef.current.urlHistory.length >= 4) {
        const recent = loopTrackingRef.current.urlHistory.slice(-4);
        const hasHomepage = recent.some(url => url.includes('localhost:5173') && !url.includes('/sign-in'));
        const hasSignIn = recent.some(url => url.includes('/sign-in'));
        
        // If we've bounced between homepage and sign-in multiple times
        if (hasHomepage && hasSignIn) {
          console.warn('🔄 URL redirect loop detected between homepage and sign-in');
          console.warn('Recent URLs:', recent);
          
          // Force complete reset
          showAlert('Redirect loop detected. Clearing authentication data...');
          
          // Clear all storage
          localStorage.clear();
          sessionStorage.clear();
          
          // Force sign out and redirect to clean homepage
          signOut().then(() => {
            window.location.href = '/';
          }).catch(() => {
            window.location.href = '/';
          });
          
          return;
        }
      }
    }

    // Track page loads for this user
    if (user?.id && isLoaded) {
      const now = Date.now();
      const timeSinceLastLoad = now - loopTrackingRef.current.lastPageLoad;
      
      if (timeSinceLastLoad < 15000) { // Within 15 seconds
        loopTrackingRef.current.pageLoads += 1;
        
        // If more than 5 page loads in 15 seconds, we're in a loop
        if (loopTrackingRef.current.pageLoads > 5) {
          console.warn('🔄 Page load loop detected, forcing complete reset');
          console.warn(`Loop details: User ${user.id}, page loads: ${loopTrackingRef.current.pageLoads}`);
          
          // Force complete reset
          showAlert('Authentication loop detected. Clearing all data and redirecting...');
          
          // Clear all storage
          localStorage.clear();
          sessionStorage.clear();
          
          // Force sign out and redirect
          signOut().then(() => {
            window.location.href = '/';
          }).catch(() => {
            // If signOut fails, force redirect anyway
            window.location.href = '/';
          });
          
          return;
        }
      } else {
        // Reset counter if enough time has passed
        loopTrackingRef.current.pageLoads = 1;
      }
      
      loopTrackingRef.current.lastPageLoad = now;
    }

    // Global axios response interceptor to catch 401 errors
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && user?.id) {
          const now = Date.now();
          const timeSinceLastError = now - loopTrackingRef.current.lastApiError;
          
          // If less than 20 seconds since last 401, increment counter
          if (timeSinceLastError < 20000) {
            loopTrackingRef.current.apiErrors += 1;
            
            // If more than 4 401s in 20 seconds, we're in an auth loop
            if (loopTrackingRef.current.apiErrors > 4) {
              console.warn('🔄 API auth loop detected, forcing reset');
              console.warn(`API loop details: User ${user.id}, 401 count: ${loopTrackingRef.current.apiErrors}`);
              
              // Clear storage and force sign out
              localStorage.clear();
              sessionStorage.clear();
              
              showAlert('Authentication issue detected. You will be signed out.');
              
              signOut().then(() => {
                window.location.href = '/';
              }).catch(() => {
                window.location.href = '/';
              });
              
              return Promise.reject(error);
            }
          } else {
            // Reset counter if enough time has passed
            loopTrackingRef.current.apiErrors = 1;
          }
          
          loopTrackingRef.current.lastApiError = now;
        }
        
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [user?.id, isLoaded, signOut]);

  return <>{children}</>;
}

// Component to redirect root path based on user's starting page preference
function HomepageRedirect() {
  const navigate = useNavigate();
  
  // Get current URL params to detect impersonation or ghost mode
  const urlParams = new URLSearchParams(window.location.search);
  const impersonateId = urlParams.get('impersonate');
  const ghostOrgId = urlParams.get('org');
  const ghostSessionId = urlParams.get('ghost') || urlParams.get('ghostSession');
  
  
  // Use new hook to get accessible starting page
  const { data: accessiblePage, isLoading, error } = useQuery(
    ['accessible-starting-page', ghostOrgId, ghostSessionId, impersonateId],
    async () => {
      const response = await api.get('/user-starting-page/accessible-starting-page');
      return response.data.data;
    },
    { 
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes - prevent repeated calls
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  );

  // Remove old queries - we now use the new accessible starting page endpoint


  // Handle navigation when data is available - MUST be before any early returns
  React.useEffect(() => {
    if (!isLoading && accessiblePage?.pageId) {
      const searchParams = new URLSearchParams(window.location.search);
      const queryString = searchParams.toString();
      const targetUrl = queryString ? `/page/${accessiblePage.pageId}?${queryString}` : `/page/${accessiblePage.pageId}`;
      
      
      // Use React Router's navigate to preserve impersonation context
      // This prevents the full page reload that loses impersonation context
      navigate(targetUrl, { replace: true });
    }
  }, [navigate, accessiblePage, isLoading, impersonateId, ghostOrgId, ghostSessionId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading your starting page...</div>
      </div>
    );
  }

  if (error || !accessiblePage) {
    // No accessible page found, show error
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-lg text-red-600 mb-4">Unable to load starting page</div>
        <div className="text-sm text-gray-600 mb-4">
          {(error as any)?.response?.status === 403 
            ? 'You do not have access to any pages in this organization.'
            : 'Could not access your preferred starting page or the organization homepage.'
          }
        </div>
        <Link to="/pages" className="text-blue-600 hover:text-blue-800 underline">
          Go to Pages
        </Link>
      </div>
    );
  }

  // This should not render as the useEffect will navigate away
  return null;
}

// Component to detect and show impersonation indicator
function ImpersonationWrapper({ children }: { children: React.ReactNode }) {
  const urlParams = new URLSearchParams(window.location.search);
  const impersonateId = urlParams.get('impersonate');
  const ghostOrgId = urlParams.get('org'); // Changed from 'ghost' to 'org' parameter
  const ghostSessionId = urlParams.get('ghost') || urlParams.get('ghostSession');

  // Emergency shortcut: Ctrl+Shift+E to end impersonation immediately
  React.useEffect(() => {
    const handleEmergencyExit = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'E') {
        event.preventDefault();
        
        // Clear all impersonation and ghost mode data
        sessionStorage.removeItem('nexus_impersonation');
        sessionStorage.removeItem('ghostModeSession');
        
        // Redirect back to admin dashboard (remove all query parameters)
        window.location.href = '/admin?tab=users';
      }
    };

    document.addEventListener('keydown', handleEmergencyExit);
    return () => document.removeEventListener('keydown', handleEmergencyExit);
  }, []);

  // Apply impersonation session when URL parameter is present
  React.useEffect(() => {
    if (impersonateId) {
      // Check if we have session data for this impersonation
      const sessionData = sessionStorage.getItem('nexus_impersonation');
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
      }
    }
  }, [impersonateId]);

  return (
    <>
      {children}
      {(impersonateId || (ghostOrgId && ghostSessionId)) && (
        <ImpersonationIndicator 
          impersonationId={impersonateId || undefined}
          ghostOrganizationId={ghostOrgId || undefined}
          ghostSessionId={ghostSessionId || undefined}
        />
      )}
    </>
  );
}

// Simple authentication wrapper using Clerk's built-in state management
function AuthenticationWrapper() {
  const { user, isLoaded } = useUser();
  
  // Check feature flag for new guard system
  const useNewGuards = (import.meta as any).env.VITE_USE_NEW_GUARDS === 'true';
  
  // Handle hash-based SSO callback URLs
  if (window.location.hash.includes('/sso-callback')) {
    return <SSOCallbackHandler />;
  }
  
  // Don't render anything until Clerk is fully loaded
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  // If user exists, show authenticated routes
  if (user) {
    const authenticatedRoutes = (
      <ErrorBoundary name="AuthenticatedRoutes">
        <ImpersonationWrapper>
          <Layout>
            <Routes>
              <Route path="/pages" element={<Pages />} />
              <Route path="/forms" element={<Forms />} />
              <Route path="/workflows" element={<Workflows />} />
              <Route path="/groups" element={<Groups />} />
              <Route path="/organisation" element={<Organisation />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/search" element={<Search />} />
              <Route path="/page/:id" element={
                <GridProvider>
                  <PageView />
                </GridProvider>
              } />
              <Route path="/shared/:sharedId/:pageId" element={<SharedPageView />} />
              <Route path="/" element={<HomepageRedirect />} />
            </Routes>
          </Layout>
        </ImpersonationWrapper>
      </ErrorBoundary>
    );

    if (useNewGuards) {
      // NEW GUARD SYSTEM: Separate AuthGuard → OrganizationGuard → PermissionGuard
      // console.log('🔗 Using NEW guard system (GuardChain)'); // Removed repetitive log
      return (
        <ErrorBoundary name="NewGuardSystem">
          <EmailActionHandler>
            <GuardChain>
              {authenticatedRoutes}
            </GuardChain>
          </EmailActionHandler>
        </ErrorBoundary>
      );
    } else {
      // OLD GUARD SYSTEM: Original OnboardingGuard
      console.log('🔒 Using OLD guard system (OnboardingGuard)');
      // debugLog('AuthenticationWrapper: Using OLD guard system');
      return (
        <ErrorBoundary name="OnboardingGuard">
          <EmailActionHandler>
            <OnboardingGuard>
              {authenticatedRoutes}
            </OnboardingGuard>
          </EmailActionHandler>
        </ErrorBoundary>
      );
    }
  }

  // If no user, show public routes
  // debugLog('AuthenticationWrapper: No user, showing public homepage');
  return <Homepage />;
}

// Immediate circuit breaker - runs before component render
const checkLoopBreaker = () => {
  const key = 'nexus_loop_breaker';
  const resetKey = 'nexus_reset_in_progress';
  const now = Date.now();
  
  // Check if we're already in reset mode
  const resetData = localStorage.getItem(resetKey);
  if (resetData) {
    const resetTime = parseInt(resetData);
    if (now - resetTime < 10000) { // 10 seconds reset window
      console.warn('🔄 Reset in progress, showing clean state');
      return false; // Allow normal render during reset
    } else {
      // Reset window expired, clear reset flag
      localStorage.removeItem(resetKey);
    }
  }
  
  const data = localStorage.getItem(key);
  
  if (data) {
    try {
      const parsed = JSON.parse(data);
      const timeDiff = now - parsed.timestamp;
      
      // If less than 20 seconds since last trigger, increment count
      if (timeDiff < 20000) {
        parsed.count += 1;
        
        // If more than 2 visits in 20 seconds, break the loop
        if (parsed.count > 2) {
          console.warn('🔄 CIRCUIT BREAKER: Loop detected, forcing reset');
          console.warn(`Count: ${parsed.count}, Time diff: ${timeDiff}ms`);
          
          // Set reset flag BEFORE clearing everything
          localStorage.setItem(resetKey, now.toString());
          
          // Clear everything except the reset flag
          const resetFlag = localStorage.getItem(resetKey);
          localStorage.clear();
          sessionStorage.clear();
          if (resetFlag) {
            localStorage.setItem(resetKey, resetFlag);
          }
          
          // Show alert and redirect
          showAlert('Authentication loop detected. Resetting application...');
          window.location.replace('/');
          return true; // Loop detected
        }
        
        localStorage.setItem(key, JSON.stringify(parsed));
      } else {
        // Reset if enough time has passed
        localStorage.setItem(key, JSON.stringify({ count: 1, timestamp: now }));
      }
    } catch (e) {
      // If parsing fails, reset
      localStorage.setItem(key, JSON.stringify({ count: 1, timestamp: now }));
    }
  } else {
    // First visit
    localStorage.setItem(key, JSON.stringify({ count: 1, timestamp: now }));
  }
  
  return false; // No loop detected
};

// SSO Callback handler component - handles both hash and regular URLs
function SSOCallbackHandler() {
  const { isLoaded, user } = useUser();
  
  React.useEffect(() => {
    
    // Check if we're on an SSO callback URL (either hash-based or regular)
    const isSSORCallback = window.location.hash.includes('/sso-callback') || 
                          window.location.pathname.includes('/sso-callback');
    
    if (isSSORCallback && isLoaded) {
      // Get redirect URL from hash params or search params
      let redirectUrl = '/';
      
      if (window.location.hash.includes('redirect_url=')) {
        // Extract from hash
        const hashParams = new URLSearchParams(window.location.hash.split('?')[1]);
        redirectUrl = decodeURIComponent(hashParams.get('redirect_url') || '/');
      } else {
        // Extract from search params
        const urlParams = new URLSearchParams(window.location.search);
        redirectUrl = urlParams.get('redirect_url') || '/';
      }
      
      
      // Force redirect to clear the SSO callback URL
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 500); // Longer delay to ensure Clerk finishes processing
    }
  }, [isLoaded, user]);
  
  // Also handle the case where we're on the homepage but the URL has SSO callback hash
  React.useEffect(() => {
    if (window.location.hash.includes('/sso-callback') && isLoaded) {
      const hashParams = new URLSearchParams(window.location.hash.split('?')[1]);
      const redirectUrl = decodeURIComponent(hashParams.get('redirect_url') || '/');
      
      // Clear the hash and redirect
      window.location.href = redirectUrl;
    }
  }, [isLoaded]);
  
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-lg text-gray-600">Completing sign in...</div>
    </div>
  );
}

// Simple circuit breaker component
function LoopCircuitBreaker() {
  const [loopDetected, setLoopDetected] = React.useState(false);
  
  React.useEffect(() => {
    const hasLoop = checkLoopBreaker();
    if (hasLoop) {
      setLoopDetected(true);
    }
  }, []);

  if (loopDetected) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-600">Resetting application...</div>
      </div>
    );
  }

  return null;
}

function App() {
  // DISABLED: Circuit breaker was too aggressive and blocking all users
  // const hasLoop = checkLoopBreaker();
  // if (hasLoop) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen">
  //       <div className="text-lg text-red-600">Loop detected. Resetting...</div>
  //     </div>
  //   );
  // }

  return (
    <QueryClientProvider client={queryClient}>
      <StripeProvider>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/sso-callback" element={<SSOCallbackHandler />} />
            <Route path="/*" element={<AuthenticationWrapper />} />
          </Routes>
        </Suspense>
        {/* <DebugPanel /> */}
      </StripeProvider>
    </QueryClientProvider>
  )
}

export default App
